import {
  Body1,
  Body1Strong,
  Button,
  Field,
  Input,
  makeStyles,
  Spinner,
  Subtitle2,
  tokens,
} from '@fluentui/react-components'
import {
  CollectionsEmpty24Regular,
  Dismiss20Regular,
  DismissCircle48Color,
  FolderOpenDown24Regular,
  Search20Regular,
} from '@fluentui/react-icons'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { sendTelemetryDocument } from '../../api/data-connectors/telemetry'
import { useGetRlcpFiles, useOverlayFooter, useUploadRlcpFiles } from '../../hooks'
import type { RlcpFile, SelectedRlcpFile } from '../../types'
import { isRlcpNotFoundError } from '../../utils'
import { BraveFileCard, type BraveFlatTreeListItem } from '../FileCard/BraveFileCard'

// Full match — used to decide when to fire the API call
const rlcpStudyNumberSchema = z.string().regex(/^B0\d{5}$/i)

// Prefix match — true as long as the input is still on a valid path.
// "b", "B0", "b01234" → valid prefix. "e", "B1", "B0123456" → invalid.
const rlcpStudyNumberPrefixSchema = z.string().regex(/^B(0\d{0,5})?$/i)

const useStyles = makeStyles({
  braveContent: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100%',
    paddingBottom: tokens.spacingVerticalXS,
  },
  input: {
    width: '100%',
  },
  clearButton: {
    marginLeft: tokens.spacingHorizontalXS,
  },
  inertWrapper: {
    display: 'contents',
  },
  centeredState: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flex: 1,
  },
  uploadingOverlay: {
    position: 'absolute',
    inset: '-16px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: tokens.colorNeutralBackground1,
    opacity: 1,
    zIndex: 1,
  },
  errorIconWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  filesListSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingHorizontalS,
  },
  noMatchesWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    '& p': {
      textAlign: 'center',
    },
  },
  fieldWrapper: {
    // always reserves space for the validation message to avoid content shift
    minHeight: '52px',
  },
})

// A file is renderable if it has children to expand, or is a leaf file with a versionId.
// Empty folder nodes are excluded at every level of the tree.
function isRenderableFile(file: RlcpFile): boolean {
  return file.children.length > 0 || !!file.versionId || !!file.createdAt || !!file.version
}

// Ensures we only include nodes that have at least one file (versionId) somewhere in their subtree.
function hasFileDescendant(file: RlcpFile): boolean {
  if (file.versionId) return true
  return file.children.some(hasFileDescendant)
}

function flattenBraveFile(file: RlcpFile, parentId?: string): BraveFlatTreeListItem[] {
  return [
    {
      value: file.id,
      title: file.name,
      versionId: file.versionId,
      version: file.version,
      createdAt: file.createdAt,
      updatedAt: file.modifiedAt,
      documentUrl: file.documentUrl,
      ...(parentId !== undefined && { parentValue: parentId }),
    },
    ...file.children
      .filter(isRenderableFile)
      .filter(hasFileDescendant)
      .flatMap((child) => flattenBraveFile(child, file.id)),
  ]
}

// Report number / content plan / section / file, e.g.
// "B021652/21652 PH-42780 Final CSR (INT)/Tables, Figures, Listings/Table 01".
// The content plan and section names say what kind of document it is, the file name alone often doesn't.
function reportBraveFilesToTelemetry(
  reportNumber: string,
  contentPlan: RlcpFile,
  selectedFilesBySection: Map<string, SelectedRlcpFile[]>
) {
  selectedFilesBySection.forEach((files, sectionId) => {
    const sectionName = contentPlan.children.find((child) => child.id === sectionId)?.name ?? ''
    files.forEach((file) => {
      sendTelemetryDocument(
        'brave',
        `${reportNumber}/${contentPlan.name}/${sectionName}/${file.name}`
      )
    })
  })
}

interface BraveUploadContentProps {
  closeDrawer: () => void
}

const BraveUploadContent = ({ closeDrawer }: BraveUploadContentProps) => {
  const [value, setValue] = useState('')
  const [selectedFilesMap, setSelectedFilesMap] = useState<Map<string, SelectedRlcpFile[]>>(
    () => new Map()
  )

  const normalizedValue = value.trim()
  const isStudyNumberValid = rlcpStudyNumberSchema.safeParse(normalizedValue).success
  const isOnValidPath = rlcpStudyNumberPrefixSchema.safeParse(normalizedValue).success
  const showStudyNumberValidationError = normalizedValue.length > 0 && !isOnValidPath

  const {
    data: contentPlan,
    error,
    isLoading,
  } = useGetRlcpFiles(normalizedValue.toUpperCase(), isStudyNumberValid)

  const { mutateAsync: uploadFiles, isPending: isUploading } = useUploadRlcpFiles(closeDrawer)

  const { t } = useTranslation()
  const classes = useStyles()

  // Precompute flattened items once per fetch so the array references passed to
  // BraveFileCard are stable across re-renders.
  const flattenedChildren = useMemo(
    () =>
      (contentPlan?.children ?? [])
        .filter(isRenderableFile)
        .filter(hasFileDescendant)
        .map((file) => ({ id: file.id, items: flattenBraveFile(file) })),
    [contentPlan]
  )

  useEffect(() => {
    setSelectedFilesMap(new Map())
  }, [normalizedValue])

  function onSelectChange(parentId: string, selectedIds: string[]) {
    const cardItems = flattenedChildren.find((entry) => entry.id === parentId)?.items
    if (!cardItems) return

    const files = cardItems
      .filter((item) => selectedIds.includes(item.value) && !!item.versionId)
      .map((item) => ({ versionId: item.versionId!, name: item.title }))

    setSelectedFilesMap((prev) => new Map(prev).set(parentId, files))
  }

  const selectedFiles = useMemo(
    () => Array.from(selectedFilesMap.values()).flat(),
    [selectedFilesMap]
  )

  // Inject the submit button into the Overlay's DrawerFooter only when files are loaded
  const footerButton = useMemo(() => {
    if (!selectedFiles.length) return null

    function addFiles() {
      uploadFiles(selectedFiles)

      if (!contentPlan) return
      // Telemetry must never stop the files from being added.
      try {
        reportBraveFilesToTelemetry(normalizedValue.toUpperCase(), contentPlan, selectedFilesMap)
      } catch (error) {
        console.warn('[Telemetry] BRAVE paths not sent:', error)
      }
    }

    return (
      <Button appearance="primary" onClick={addFiles}>
        {t('brave.addFiles', { count: selectedFiles.length })}
      </Button>
    )
  }, [selectedFiles, selectedFilesMap, contentPlan, normalizedValue, uploadFiles, t])

  useOverlayFooter(footerButton)

  return (
    <div className={classes.braveContent} aria-busy={isUploading}>
      {/* Overlay strategy: keep BraveFileCard mounted during upload so its internal checked state survives.
      If the upload fails, the overlay is removed and the tree reappears with prior selection intact. */}
      {isUploading && (
        <div className={classes.uploadingOverlay}>
          <Spinner label={t('files.uploadingFiles')} labelPosition="below" />
        </div>
      )}
      {/* inert prevents keyboard focus and screen reader access to content behind the overlay */}
      <div inert={isUploading ? true : undefined} className={classes.inertWrapper}>
        <div className={classes.fieldWrapper}>
          <Field
            validationState={showStudyNumberValidationError ? 'error' : 'none'}
            validationMessage={
              showStudyNumberValidationError ? t('brave.invalidReportNumber') : undefined
            }>
            <Input
              aria-label={t('brave.searchBrave')}
              className={classes.input}
              appearance="filled-darker"
              value={value}
              onChange={(_, data) => setValue(data.value)}
              contentBefore={<Search20Regular />}
              contentAfter={
                value ? (
                  <Button
                    aria-label={t('brave.clearSearch')}
                    appearance="subtle"
                    icon={<Dismiss20Regular />}
                    size="small"
                    onClick={() => setValue('')}
                    className={classes.clearButton}
                  />
                ) : null
              }
              placeholder={t('brave.searchBrave')}
            />
          </Field>
        </div>
        {flattenedChildren.length ? (
          <div className={classes.filesListSection}>
            {flattenedChildren.map(({ id, items }) => (
              <BraveFileCard key={id} items={items} onChange={onSelectChange} />
            ))}
          </div>
        ) : isLoading ? (
          <div className={classes.centeredState}>
            <Spinner label={t('brave.loadingFiles')} labelPosition="below" />
          </div>
        ) : isRlcpNotFoundError(error) ? (
          <div className={classes.centeredState}>
            <div className={classes.noMatchesWrapper}>
              <CollectionsEmpty24Regular aria-hidden="true" />
              <Subtitle2 as="p">{t('brave.noMatches')}</Subtitle2>
              <Body1 as="p">{t('brave.noMatchesDescription')}</Body1>
            </div>
          </div>
        ) : error ? (
          <div className={classes.centeredState}>
            <div className={classes.errorIconWrapper}>
              <DismissCircle48Color aria-hidden="true" />
              <Body1Strong as="p">{t('brave.errorFetchingFiles')}</Body1Strong>
            </div>
          </div>
        ) : // API was called (isStudyNumberValid), it's not loading nor errory, but there are no files in the data
        isStudyNumberValid && !flattenedChildren.length ? (
          <div className={classes.centeredState}>
            <div className={classes.noMatchesWrapper}>
              <FolderOpenDown24Regular aria-hidden="true" />
              <Subtitle2 as="p">{t('brave.noFilesYet')}</Subtitle2>
              <Body1 as="p">{t('brave.noFilesYetDescription')}</Body1>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  )
}

export { BraveUploadContent }
